// WP-6.1 (M28 GL-1..8, S-31 `/goals`): Goals & Reserves — goal cards (ProgressRing, target,
// date, strict/flexible badge, allocated/remaining), a Reserves panel (envelopes with a
// reason-required release confirm, GL-4), and the GL-7 ramp-up proposal banner that pre-fills
// (never auto-creates) a suggested safety-reserve goal.
import {
  fromMinor,
  parseAmountInput,
  toDecimalString,
  type CurrencyCode,
  type GoalBehavior,
} from '@pfos/engine';
import type { GoalResponse } from '@pfos/shared';
import { useMemo, useState } from 'react';
import { Amount } from '../components/Amount';
import { ConfirmDialog } from '../components/ConfirmDialog';
import {
  AmountField,
  Field,
  ghostButtonInlineClass,
  inputClass,
  primaryButtonInlineClass,
  selectClass,
} from '../components/FormKit';
import { ProgressRing } from '../components/ProgressRing';
import {
  useCreateGoal,
  useCreateReserve,
  useGoals,
  useRampUp,
  useReleaseReserve,
  useReserves,
} from '../lib/goals';

const CURRENCIES: readonly CurrencyCode[] = ['PKR', 'USD', 'EUR', 'GBP'];

interface GoalPrefill {
  key: number; // forces NewGoalForm to remount with fresh initial values
  name: string;
  targetAmountMinor: number;
  behavior: GoalBehavior;
}

function GoalCard({ goal }: { goal: GoalResponse }): React.JSX.Element {
  const percent =
    goal.targetAmountMinor > 0 ? (goal.allocatedMinor / goal.targetAmountMinor) * 100 : 0;
  return (
    <div className="rounded-[var(--radius-card)] border border-border bg-surface p-4">
      <div className="mb-3 flex items-start justify-between gap-2">
        <p className="font-medium text-ink">{goal.name}</p>
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${
            goal.behavior === 'strict' ? 'bg-danger/10 text-danger' : 'bg-primary/10 text-primary'
          }`}
        >
          {goal.behavior === 'strict' ? 'Strict' : 'Flexible'}
        </span>
      </div>
      <div className="flex justify-center">
        <ProgressRing
          percent={Math.min(100, percent)}
          label={
            <>
              <Amount currency={goal.currency} minorUnits={goal.allocatedMinor} /> of{' '}
              <Amount currency={goal.currency} minorUnits={goal.targetAmountMinor} />
            </>
          }
        />
      </div>
      <div className="mt-3 space-y-1 text-sm">
        <div className="flex items-center justify-between">
          <span className="text-muted">Target</span>
          <Amount currency={goal.currency} minorUnits={goal.targetAmountMinor} />
        </div>
        {goal.targetDate !== undefined && (
          <div className="flex items-center justify-between">
            <span className="text-muted">Target date</span>
            <span className="text-ink">{new Date(goal.targetDate).toLocaleDateString()}</span>
          </div>
        )}
        <div className="flex items-center justify-between">
          <span className="text-muted">Remaining</span>
          <Amount currency={goal.currency} minorUnits={goal.remainingMinor} />
        </div>
      </div>
    </div>
  );
}

function NewGoalForm({
  currency,
  initialName = '',
  initialTargetAmountMinor,
  initialBehavior = 'flexible',
}: {
  currency: CurrencyCode;
  initialName?: string;
  initialTargetAmountMinor?: number;
  initialBehavior?: GoalBehavior;
}): React.JSX.Element {
  const [name, setName] = useState(initialName);
  const [amount, setAmount] = useState(
    initialTargetAmountMinor !== undefined
      ? toDecimalString(fromMinor(currency, initialTargetAmountMinor))
      : '',
  );
  const [goalCurrency, setGoalCurrency] = useState<CurrencyCode>(currency);
  const [targetDate, setTargetDate] = useState('');
  const [priority, setPriority] = useState(1);
  const [behavior, setBehavior] = useState<GoalBehavior>(initialBehavior);
  const [error, setError] = useState<string | null>(null);
  const createMutation = useCreateGoal();

  const handleSubmit = (): void => {
    if (name.trim() === '') {
      setError('Enter a name');
      return;
    }
    const parsed = parseAmountInput(amount, { defaultCurrency: goalCurrency });
    if (!parsed.ok) {
      setError('Enter a valid target amount');
      return;
    }
    setError(null);
    createMutation.mutate(
      {
        name: name.trim(),
        targetAmountMinor: parsed.money.minorUnits,
        currency: goalCurrency,
        ...(targetDate !== '' ? { targetDate: new Date(targetDate).toISOString() } : {}),
        priority,
        behavior,
      },
      {
        onSuccess: () => {
          setName('');
          setAmount('');
          setTargetDate('');
          setPriority(1);
          setBehavior('flexible');
        },
        onError: (e) => {
          setError(e.message);
        },
      },
    );
  };

  return (
    <div className="rounded-[var(--radius-card)] border border-border bg-surface p-4">
      <p className="mb-3 text-sm font-medium text-ink">New goal</p>
      <Field label="Name" htmlFor="goal-name">
        <input
          id="goal-name"
          className={inputClass}
          value={name}
          onChange={(e) => {
            setName(e.target.value);
          }}
        />
      </Field>
      <Field label="Target amount" htmlFor="goal-amount">
        <AmountField id="goal-amount" currency={goalCurrency} value={amount} onChange={setAmount} />
      </Field>
      <Field label="Currency" htmlFor="goal-currency">
        <select
          id="goal-currency"
          className={selectClass}
          value={goalCurrency}
          onChange={(e) => {
            setGoalCurrency(e.target.value as CurrencyCode);
          }}
        >
          {CURRENCIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Target date (optional)" htmlFor="goal-date">
        <input
          id="goal-date"
          type="date"
          className={inputClass}
          value={targetDate}
          onChange={(e) => {
            setTargetDate(e.target.value);
          }}
        />
      </Field>
      <Field label="Priority" htmlFor="goal-priority">
        <input
          id="goal-priority"
          type="number"
          min={1}
          className={inputClass}
          value={priority}
          onChange={(e) => {
            setPriority(Number(e.target.value));
          }}
        />
      </Field>
      <Field label="Behavior" htmlFor="goal-behavior">
        <select
          id="goal-behavior"
          className={selectClass}
          value={behavior}
          onChange={(e) => {
            setBehavior(e.target.value as GoalBehavior);
          }}
        >
          <option value="flexible">Flexible</option>
          <option value="strict">Strict</option>
        </select>
      </Field>
      {error !== null && <p className="mb-2 text-sm text-danger">{error}</p>}
      <button
        type="button"
        className={primaryButtonInlineClass}
        disabled={createMutation.isPending}
        onClick={handleSubmit}
      >
        + New goal
      </button>
    </div>
  );
}

function ReserveRow({
  id,
  purpose,
  amountMinor,
  currency,
  goalName,
}: {
  id: string;
  purpose: string;
  amountMinor: number;
  currency: CurrencyCode;
  goalName: string | undefined;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const releaseMutation = useReleaseReserve(id);
  return (
    <li className="flex flex-wrap items-center justify-between gap-2 border-b border-border py-2 last:border-0">
      <div>
        <p className="text-sm text-ink">{purpose}</p>
        {goalName !== undefined && <p className="text-xs text-muted">For {goalName}</p>}
      </div>
      <div className="flex shrink-0 items-center gap-3">
        <Amount currency={currency} minorUnits={amountMinor} className="font-medium" />
        <button
          type="button"
          className="text-sm text-danger hover:underline"
          onClick={() => {
            setOpen(true);
          }}
        >
          Release
        </button>
      </div>
      <ConfirmDialog
        open={open}
        onOpenChange={setOpen}
        title="Release this reserve?"
        consequence="The reserved amount becomes spendable again immediately. Reserves are releasable anytime, and every change is logged (GL-4)."
        confirmLabel="Release"
        requireReason
        reasonLabel="Reason"
        confirming={releaseMutation.isPending}
        onConfirm={(reason) => {
          releaseMutation.mutate(reason, {
            onSuccess: () => {
              setOpen(false);
            },
          });
        }}
      />
    </li>
  );
}

function NewReserveForm({
  currency,
  activeGoals,
}: {
  currency: CurrencyCode;
  activeGoals: GoalResponse[];
}): React.JSX.Element {
  const [purpose, setPurpose] = useState('');
  const [amount, setAmount] = useState('');
  const [reserveCurrency, setReserveCurrency] = useState<CurrencyCode>(currency);
  const [goalId, setGoalId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const createMutation = useCreateReserve();

  const handleSubmit = (): void => {
    if (purpose.trim() === '') {
      setError('Enter a purpose');
      return;
    }
    const parsed = parseAmountInput(amount, { defaultCurrency: reserveCurrency });
    if (!parsed.ok) {
      setError('Enter a valid amount');
      return;
    }
    setError(null);
    createMutation.mutate(
      {
        purpose: purpose.trim(),
        amountMinor: parsed.money.minorUnits,
        currency: reserveCurrency,
        ...(goalId !== '' ? { goalId } : {}),
      },
      {
        onSuccess: () => {
          setPurpose('');
          setAmount('');
          setGoalId('');
        },
        onError: (e) => {
          setError(e.message);
        },
      },
    );
  };

  return (
    <div className="mt-4 border-t border-border pt-4">
      <p className="mb-3 text-sm font-medium text-ink">New reserve</p>
      <Field label="Purpose" htmlFor="reserve-purpose">
        <input
          id="reserve-purpose"
          className={inputClass}
          value={purpose}
          onChange={(e) => {
            setPurpose(e.target.value);
          }}
        />
      </Field>
      <Field label="Amount" htmlFor="reserve-amount">
        <AmountField
          id="reserve-amount"
          currency={reserveCurrency}
          value={amount}
          onChange={setAmount}
        />
      </Field>
      <Field label="Currency" htmlFor="reserve-currency">
        <select
          id="reserve-currency"
          className={selectClass}
          value={reserveCurrency}
          onChange={(e) => {
            setReserveCurrency(e.target.value as CurrencyCode);
          }}
        >
          {CURRENCIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Goal (optional)" htmlFor="reserve-goal">
        <select
          id="reserve-goal"
          className={selectClass}
          value={goalId}
          onChange={(e) => {
            setGoalId(e.target.value);
          }}
        >
          <option value="">No goal</option>
          {activeGoals.map((g) => (
            <option key={g.id} value={g.id}>
              {g.name}
            </option>
          ))}
        </select>
      </Field>
      {error !== null && <p className="mb-2 text-sm text-danger">{error}</p>}
      <button
        type="button"
        className={primaryButtonInlineClass}
        disabled={createMutation.isPending}
        onClick={handleSubmit}
      >
        + Reserve
      </button>
    </div>
  );
}

export function GoalsPage(): React.JSX.Element {
  const [currency, setCurrency] = useState<CurrencyCode>('PKR');
  const [dismissedRampUp, setDismissedRampUp] = useState(false);
  const [prefill, setPrefill] = useState<GoalPrefill | null>(null);

  const { data: goals, isLoading: goalsLoading } = useGoals({ status: 'active' });
  const { data: reserves, isLoading: reservesLoading } = useReserves({ status: 'active' });
  const { data: rampUp } = useRampUp(currency);

  const goalNameById = useMemo(() => new Map((goals ?? []).map((g) => [g.id, g.name])), [goals]);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-semibold text-ink">Goals</h1>
        <label className="block">
          <span className="mb-1 block text-sm text-muted">Currency</span>
          <select
            aria-label="Currency"
            className={`${selectClass} w-auto`}
            value={currency}
            onChange={(e) => {
              setCurrency(e.target.value as CurrencyCode);
              setDismissedRampUp(false);
            }}
          >
            {CURRENCIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
      </div>

      {rampUp?.targetProposalEligible === true && !dismissedRampUp && (
        <div
          role="status"
          className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-[var(--radius-card)] border border-primary/30 bg-primary/5 p-3 text-sm"
        >
          <p className="text-ink">
            Based on {rampUp.daysOfData} days of data, a safety reserve of{' '}
            <Amount
              currency={currency}
              minorUnits={rampUp.suggestedTargetMinor}
              className="font-medium"
            />{' '}
            looks reasonable (GL-7). Accept, adjust, or make it strict at any time.
          </p>
          <div className="flex shrink-0 gap-2">
            <button
              type="button"
              className={primaryButtonInlineClass}
              onClick={() => {
                setPrefill({
                  key: Date.now(),
                  name: 'Safety reserve',
                  targetAmountMinor: rampUp.suggestedTargetMinor,
                  behavior: 'flexible',
                });
              }}
            >
              Use this
            </button>
            <button
              type="button"
              className={ghostButtonInlineClass}
              onClick={() => {
                setDismissedRampUp(true);
              }}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          {goalsLoading && <p className="text-sm text-muted">Loading…</p>}
          {goals?.length === 0 && <p className="text-sm text-muted">No goals yet.</p>}
          <div className="grid gap-4 sm:grid-cols-2">
            {(goals ?? []).map((g) => (
              <GoalCard key={g.id} goal={g} />
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <NewGoalForm
            key={prefill?.key ?? 'blank'}
            currency={currency}
            {...(prefill !== null
              ? {
                  initialName: prefill.name,
                  initialTargetAmountMinor: prefill.targetAmountMinor,
                  initialBehavior: prefill.behavior,
                }
              : {})}
          />

          <div className="rounded-[var(--radius-card)] border border-border bg-surface p-4">
            <p className="mb-3 text-sm font-medium text-ink">Reserves</p>
            {reservesLoading && <p className="text-sm text-muted">Loading…</p>}
            {reserves?.length === 0 && <p className="text-sm text-muted">No active reserves.</p>}
            <ul>
              {(reserves ?? []).map((r) => (
                <ReserveRow
                  key={r.id}
                  id={r.id}
                  purpose={r.purpose}
                  amountMinor={r.amountMinor}
                  currency={r.currency}
                  goalName={r.goalId !== undefined ? goalNameById.get(r.goalId) : undefined}
                />
              ))}
            </ul>
            <NewReserveForm currency={currency} activeGoals={goals ?? []} />
          </div>
        </div>
      </div>
    </div>
  );
}
